import type { Member, Status, TaskSummary, User } from "./types.js";

export interface TaskFlowAction {
  id: string;
  label: string;
  confirm: string;
  target: Status;
  note?: "return" | "cancel";
}

const start: TaskFlowAction = {
  id: "start",
  label: "开始处理",
  confirm: "确认开始处理",
  target: "in_progress",
};
const submit: TaskFlowAction = {
  id: "submit",
  label: "提交测试",
  confirm: "确认提测",
  target: "in_review",
};
const pass: TaskFlowAction = {
  id: "pass",
  label: "测试通过",
  confirm: "通过并完成",
  target: "done",
};
const back: TaskFlowAction = {
  id: "return",
  label: "退回开发",
  confirm: "确认退回开发",
  target: "in_progress",
  note: "return",
};
const complete: TaskFlowAction = {
  id: "complete",
  label: "直接完成",
  confirm: "确认完成任务",
  target: "done",
};
const cancel: TaskFlowAction = {
  id: "cancel",
  label: "取消任务",
  confirm: "确认取消任务",
  target: "cancelled",
  note: "cancel",
};
const reopen: TaskFlowAction = {
  id: "reopen",
  label: "重新打开",
  confirm: "确认重新打开",
  target: "todo",
};

export const taskFlowActions: Record<
  Status,
  {
    primary: TaskFlowAction;
    secondary?: TaskFlowAction;
    more: TaskFlowAction[];
  }
> = {
  todo: { primary: start, more: [complete, cancel] },
  in_progress: { primary: submit, more: [complete, cancel] },
  in_review: { primary: pass, secondary: back, more: [cancel] },
  done: { primary: reopen, more: [] },
  cancelled: { primary: reopen, more: [] },
};

// Assignees remain the task owners. The people handling the current stage can change.
export function taskHandlers(
  task: Pick<TaskSummary, "status" | "assignees" | "testers">,
  members: Member[],
): User[] {
  if (task.status === "done" || task.status === "cancelled") return [];
  if (task.status !== "in_review") return task.assignees || [];
  const available = members.filter((m) => m.role !== "viewer");
  return task.testers?.length
    ? task.testers.filter((p) => available.some((m) => m.id === p.id))
    : available.filter((m) => m.disciplines.includes("testing"));
}

export function flowTitle(from: Status, to: Status) {
  if (to === "in_review") return "提交了测试";
  if (from === "in_review" && to === "in_progress")
    return "测试未通过，退回开发";
  if (from === "in_review" && to === "done") return "测试通过，完成了任务";
  if (to === "done") return "完成了任务";
  if (to === "cancelled") return "取消了任务";
  if (from === "done" || from === "cancelled") return "重新打开了任务";
  return to === "in_progress" ? "开始处理任务" : "将任务移回待处理";
}
